import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { BalanceTrendPoint, TimePeriod } from '@/types/account-drilldown';

export interface PortfolioBalanceTrendChartProps {
  title?: string;
  data: BalanceTrendPoint[];
  currency: string;
  timePeriod: TimePeriod;
  isLoading: boolean;
  selectedCurrency?: string;
}

/**
 * Format date for display based on time period
 */
function formatDate(dateStr: string, timePeriod: TimePeriod): string {
  const date = new Date(dateStr);

  if (timePeriod === 'last7days' || timePeriod === 'last30days') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else if (timePeriod === 'last90days') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
}

/**
 * Format currency value for display
 */
function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Custom tooltip component for the chart
 */
function CustomTooltip({ active, payload, currency }: any) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0].payload;
  const date = new Date(data.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{date}</p>
      <p className="text-lg font-semibold">
        {formatCurrency(data.balance, currency)}
      </p>
    </div>
  );
}

/**
 * Portfolio Balance Trend Chart Component
 * Displays total net worth over time across all accounts
 * Values are displayed in selected currency (or base currency if not specified)
 */
export function PortfolioBalanceTrendChart({
  title,
  data,
  currency,
  timePeriod,
  isLoading,
  selectedCurrency
}: PortfolioBalanceTrendChartProps) {
  // Use selectedCurrency if provided, otherwise fall back to base currency
  const displayCurrency = selectedCurrency || currency;
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title} Balance Trend</CardTitle>
          <CardDescription>Loading portfolio history...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-end justify-between gap-2 px-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="w-full bg-muted animate-pulse rounded-t"
                style={{ height: `${Math.random() * 60 + 40}%` }}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title} Balance Trend</CardTitle>
          <CardDescription>No portfolio data available</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No data to display</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title} Balance Trend</CardTitle>
        <CardDescription>
          Total net worth over time in {displayCurrency}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          role="img"
          aria-label={`${title} balance trend chart showing ${data.length} data points in ${displayCurrency}`}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={data}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              tabIndex={0}
              aria-label={`${title} balance trend line chart`}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(value, timePeriod)}
                className="text-xs"
              />
              <YAxis
                tickFormatter={(value) => formatCurrency(value, displayCurrency)}
                className="text-xs"
              />
              <Tooltip content={<CustomTooltip currency={displayCurrency} />} />
              <Line
                type="monotone"
                dataKey="balance"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {/* Screen reader accessible data table */}
        <div className="sr-only">
          <table>
            <caption>{title} balance trend data</caption>
            <thead>
              <tr>
                <th>Date</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.map((point, index) => (
                <tr key={index}>
                  <td>{new Date(point.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</td>
                  <td>{formatCurrency(point.balance, displayCurrency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
